// STEP SOCIAL-COMMENT-UI-1 · Seller 통합 댓글관리 UI
//
// 역할:
//   - post_comments(실 데이터만) 조회 및 표시
//   - 플랫폼 필터 (전체 / Instagram / TikTok)
//   - source_clip_id query 지원 (SellerShortClips 에서 진입)
//   - 원댓글/대댓글 thread 표시 (parent_comment_id 기준)
//   - Instagram 실제 조회/답글과 DB-only demo 경로를 명확히 분리
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Instagram, Music, MessageCircle, RefreshCw, CheckCircle2,
  Clock, AlertCircle, CornerDownRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const PLATFORM_LABEL = {
  instagram: 'Instagram',
  tiktok:    'TikTok',
};

function instagramError(error, fallback) {
  const code = error?.response?.data?.detail;
  if (typeof code !== 'string') return fallback;
  if (/reconnect|permission|expired|invalid_reconnect/.test(code)) return 'Instagram 연결 또는 권한을 확인한 뒤 계정을 재연결해 주세요.';
  if (/mismatch|not_owned|not_found|seller_not_owned|owner_required/.test(code)) return '본인 계정의 게시물과 댓글인지 확인해 주세요.';
  if (/ALREADY|RACE_LOST/.test(code)) return '이미 전송했거나 전송 중인 답글입니다. 댓글을 새로고침해 주세요.';
  if (code === 'instagram_reply_failed_check_thread_before_retry') return '전송을 확인하지 못했습니다. Instagram에서 답글 유무를 확인한 뒤 다시 시도해 주세요.';
  return fallback;
}
const PLATFORM_BADGE_CLS = {
  instagram: 'bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white',
  tiktok:    'bg-black text-white',
};
function PlatformIcon({ platform, size = 12 }) {
  if (platform === 'instagram') return <Instagram size={size} />;
  if (platform === 'tiktok')    return <Music size={size} />;
  return <MessageCircle size={size} />;
}

const REPLY_STATUS = {
  none:    { label: '답글 없음', cls: 'text-gray-400',   Icon: Clock },
  sending: { label: '전송 중',   cls: 'text-blue-500',   Icon: RefreshCw },
  success: { label: '답글 완료', cls: 'text-emerald-600', Icon: CheckCircle2 },
  failed:  { label: '전송 실패', cls: 'text-red-500',    Icon: AlertCircle },
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

// ---------------------------------------------------------------------------
// Thread grouping · parent_comment_id → nested replies 1-level
// ---------------------------------------------------------------------------
function groupThreads(items) {
  const topLevel = [];
  const byParent = new Map();
  for (const it of items) {
    if (it.parent_comment_id) {
      const arr = byParent.get(it.parent_comment_id) || [];
      arr.push(it);
      byParent.set(it.parent_comment_id, arr);
    } else {
      topLevel.push(it);
    }
  }
  // orphan replies (parent 가 현재 페이지에 없음) 은 자체로 top-level 처럼 표시
  const orphan = [];
  for (const [pid, arr] of byParent.entries()) {
    const hasParent = topLevel.some((t) => t.comment_id === pid);
    if (!hasParent) orphan.push(...arr);
  }
  return { topLevel, byParent, orphan };
}

// ---------------------------------------------------------------------------
// 단일 댓글 카드 (원댓글)
// ---------------------------------------------------------------------------
function CommentCard({ item, replies, onReplyClick, replyDraft, onDraftChange, sending }) {
  const rs = REPLY_STATUS[item.reply_status] || REPLY_STATUS.none;
  const RSIcon = rs.Icon;
  const plt = item.platform || 'unknown';
  const canReply = plt === 'instagram' && !['success', 'sending'].includes(item.reply_status);
  const isDemo = !!item.is_demo;
  return (
    <div className={`rounded-xl border overflow-hidden ${isDemo
         ? 'border-amber-300 bg-amber-50/40 dark:border-amber-700 dark:bg-amber-950/20'
         : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]'}`}
         data-testid={`comment-card-${item.post_comment_id}`}>
      {/* Header: platform + clip + time */}
      <div className="flex items-start gap-3 p-3 border-b border-black/5 dark:border-white/5">
        {item.clip_thumbnail ? (
          <img src={item.clip_thumbnail} alt=""
               className="w-14 h-14 rounded-md object-cover flex-shrink-0 bg-gray-100"
               data-testid={`comment-clip-thumb-${item.post_comment_id}`}/>
        ) : (
          <div className="w-14 h-14 rounded-md bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={18} className="text-gray-300"/>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${PLATFORM_BADGE_CLS[plt] || 'bg-gray-500 text-white'}`}
                  data-testid={`comment-platform-${item.post_comment_id}`}>
              <PlatformIcon platform={plt} size={10}/>
              {PLATFORM_LABEL[plt] || plt}
            </span>
            {isDemo && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500 text-white"
                    data-testid={`comment-demo-badge-${item.post_comment_id}`}>
                Development Demo
              </span>
            )}
            {item.source_clip_id ? (
              <span className="text-[11px] text-gray-500 truncate max-w-[200px]"
                    title={item.clip_title || item.source_clip_id}>
                {item.clip_title || `클립 ${String(item.source_clip_id).slice(0, 8)}…`}
              </span>
            ) : (
              <span className="text-[11px] text-gray-400">외부 게시물</span>
            )}
            <span className="ml-auto text-[11px] text-gray-400">{fmtTime(item.created_at_ext)}</span>
          </div>
          <div className="mt-1">
            <p className="text-xs font-bold text-gray-700 dark:text-gray-200 truncate"
               data-testid={`comment-author-${item.post_comment_id}`}>
              @{item.author || '익명'}
            </p>
            <p className="mt-0.5 text-sm text-gray-900 dark:text-white break-words whitespace-pre-wrap"
               data-testid={`comment-text-${item.post_comment_id}`}>
              {item.text || '(내용 없음)'}
            </p>
          </div>
        </div>
      </div>

      {/* Replies (1-level) */}
      {replies && replies.length > 0 && (
        <div className="pl-10 pr-3 py-2 space-y-2 bg-black/[0.015] dark:bg-white/[0.02]">
          {replies.map((r) => (
            <div key={r.post_comment_id} className="flex items-start gap-2"
                 data-testid={`comment-reply-${r.post_comment_id}`}>
              <CornerDownRight size={12} className="text-gray-400 mt-1 flex-shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] font-bold text-gray-700 dark:text-gray-200">
                    @{r.author || '익명'}
                  </p>
                  <span className="text-[10px] text-gray-400">{fmtTime(r.created_at_ext)}</span>
                </div>
                <p className="text-xs text-gray-800 dark:text-gray-200 break-words whitespace-pre-wrap">
                  {r.text || '(내용 없음)'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Real reply and DB-only demo remain distinct. */}
      <div className="p-3 border-t border-black/5 dark:border-white/5 space-y-2">
        <div className={`flex items-center gap-1 text-[11px] font-bold ${rs.cls}`}
             data-testid={`comment-reply-status-${item.post_comment_id}`}>
          <RSIcon size={11}/>
          <span>{rs.label}</span>
          {item.external_reply_id && (
            <span className="text-gray-400 font-normal ml-1 truncate max-w-[160px]"
                  title={item.external_reply_id}>
              · ID {String(item.external_reply_id).slice(0, 10)}
            </span>
          )}
        </div>

        <div className="flex items-start gap-2">
          <textarea
            value={replyDraft || ''}
            onChange={(e) => onDraftChange(item.post_comment_id, e.target.value)}
            placeholder={canReply
              ? (isDemo
                  ? '답글 입력 (Development Demo · 외부 Instagram 전송 안 함)'
                  : '답글 입력 (Instagram에 실제 전송됩니다)')
              : (plt === 'tiktok' ? 'TikTok 답글 API 승인 대기 중' : '답글 없음')}
            rows={2}
            disabled={!canReply || sending}
            className="flex-1 px-2 py-1.5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs disabled:opacity-50 resize-none"
            data-testid={`comment-reply-input-${item.post_comment_id}`}/>
          <button
            onClick={() => onReplyClick(item)}
            disabled={!canReply || !replyDraft || sending}
            className="px-3 py-1.5 rounded-full text-[11px] font-black bg-[#FF3B30] text-white hover:bg-[#D32F2F] disabled:opacity-40 whitespace-nowrap"
            data-testid={`comment-reply-send-${item.post_comment_id}`}>
            답글 보내기
          </button>
        </div>
        <p className="text-[10px] text-gray-400"
           data-testid={`comment-reply-gate-${item.post_comment_id}`}>
          {isDemo
            ? 'Development Demo · 이 답글은 Instagram 으로 전송되지 않으며 심사 영상용 UI 상태만 전환합니다.'
            : '실제 Instagram 댓글 · 답글 보내기를 누르면 Instagram에 전송됩니다.'}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 메인 페이지
// ---------------------------------------------------------------------------
export default function SellerComments({ user }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const platform    = (sp.get('platform') || 'all').toLowerCase();
  const sourceClip  = sp.get('source_clip_id') || '';

  const [items, setItems]   = useState([]);
  const [counts, setCounts] = useState({ total: 0, instagram: 0, tiktok: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState('');
  const [drafts, setDrafts] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [media, setMedia] = useState([]);
  const [mediaCursor, setMediaCursor] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState('');
  const [commentCursor, setCommentCursor] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    const params = new URLSearchParams();
    if (platform && platform !== 'all') params.set('platform', platform);
    if (selectedMedia) params.set('media_id', selectedMedia);
    else if (sourceClip) params.set('source_clip_id', sourceClip);
    params.set('limit', '100');
    try {
      const r = await api.get(`/seller/social-comments?${params.toString()}`);
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setCounts(r.data?.counts || { total: 0, instagram: 0, tiktok: 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || '댓글 조회 실패');
      setItems([]);
    } finally { setLoading(false); }
  }, [platform, sourceClip, selectedMedia]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => groupThreads(items), [items]);
  const empty   = !loading && items.length === 0;

  const setFilter = (next) => {
    const p = new URLSearchParams(sp);
    if (next === 'all') p.delete('platform'); else p.set('platform', next);
    setSp(p);
  };

  const clearClipFilter = () => {
    const p = new URLSearchParams(sp);
    p.delete('source_clip_id');
    setSp(p);
  };

  const onDraftChange = (pcid, v) => {
    setDrafts((prev) => ({ ...prev, [pcid]: v }));
  };

  const onReplyClick = async (item) => {
    const text = (drafts[item.post_comment_id] || '').trim();
    if (!text) return;
    setSendingId(item.post_comment_id);
    try {
      const r = await api.post(
        `/seller/social-comments/${encodeURIComponent(item.post_comment_id)}/${item.is_demo ? 'reply-demo' : 'reply'}`,
        { text });
      if (r?.data?.ok) {
        toast.success(item.is_demo ? 'Development Demo 답글 완료' : 'Instagram 답글 전송 완료', {
          description: item.is_demo ? 'DB 상태만 전환 · Instagram 전송 없음' : 'Instagram에서 실제 답글을 확인하세요.',
        });
        setDrafts((prev) => ({ ...prev, [item.post_comment_id]: '' }));
        await load();
      } else {
        toast.error('답글 처리 실패');
      }
    } catch (e) {
      toast.error(instagramError(e, '답글 처리 실패'));
    } finally {
      setSendingId(null);
    }
  };

  const loadMedia = async (cursor = null) => {
    setSyncing(true); setSyncError('');
    try {
      const r = await api.get('/seller/social-comments/instagram/media', {
        params: cursor ? { cursor } : {},
      });
      setMedia((prev) => cursor ? [...prev, ...(r.data?.items || [])] : (r.data?.items || []));
      setMediaCursor(r.data?.next_cursor || null);
      if (!cursor) { setSelectedMedia(''); setCommentCursor(null); }
    } catch (e) {
      setSyncError(instagramError(e, 'Instagram 게시물 조회 실패'));
    } finally { setSyncing(false); }
  };

  const syncComments = async (cursor = null) => {
    if (!selectedMedia) return;
    setSyncing(true); setSyncError('');
    try {
      const r = await api.post('/seller/social-comments/instagram/sync', {
        media_id: selectedMedia, cursor,
      });
      if (!r.data?.ok) throw new Error('sync_failed');
      setCommentCursor(r.data?.next_cursor || null);
      await load();
      toast.success('Instagram 실제 댓글 조회 완료');
    } catch (e) {
      setSyncError(instagramError(e, 'Instagram 댓글 조회 실패'));
    } finally { setSyncing(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]"
         data-testid="seller-comments-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <button onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black"
                data-testid="seller-comments-back">
          <ArrowLeft size={14}/> 뒤로
        </button>

        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              💬 댓글관리
            </h1>
            <p className="text-xs text-gray-500 mt-1">
              Instagram / TikTok 게시물 댓글을 한 곳에서 확인합니다.
              {sourceClip && (
                <>
                  {' '}· 숏폼 <code className="text-[10px] bg-black/5 px-1 rounded">{String(sourceClip).slice(0, 10)}…</code> 범위
                  <button onClick={clearClipFilter}
                          className="ml-2 underline text-[10px]"
                          data-testid="seller-comments-clear-clip">전체 보기</button>
                </>
              )}
            </p>
          </div>
          <button onClick={load}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold border border-black/10 hover:border-[#FF3B30]"
                  data-testid="seller-comments-refresh">
            <RefreshCw size={11}/> 새로고침
          </button>
        </div>

        {platform !== 'tiktok' && (
          <div className="p-3 rounded-lg border border-black/10 space-y-2" data-testid="instagram-real-comments">
            <p className="text-xs font-bold">Instagram 실제 게시물·Reels 댓글</p>
            <p className="text-xs text-gray-500">연결한 계정의 게시물을 선택해 댓글을 가져옵니다. 권한이 부족하면 Instagram을 재연결해 주세요.</p>
            <div className="flex gap-2 flex-wrap">
              <button disabled={syncing} onClick={() => loadMedia()} className="text-xs underline" data-testid="instagram-media-load">게시물 불러오기</button>
              {mediaCursor && <button disabled={syncing} onClick={() => loadMedia(mediaCursor)} className="text-xs underline">게시물 더 보기</button>}
              <select aria-label="Instagram 게시물" value={selectedMedia} disabled={syncing}
                onChange={(e) => { setSelectedMedia(e.target.value); setCommentCursor(null); }}
                className="text-xs border rounded p-1 max-w-full" data-testid="instagram-media-select">
                <option value="">게시물 선택</option>
                {media.map((m) => <option key={m.id} value={m.id}>{m.caption?.slice(0, 60) || m.media_type || '게시물'} · {m.id}</option>)}
              </select>
              <button disabled={syncing || !selectedMedia} onClick={() => syncComments()} className="text-xs underline" data-testid="instagram-comments-sync">실제 댓글 가져오기</button>
              {commentCursor && <button disabled={syncing} onClick={() => syncComments(commentCursor)} className="text-xs underline">댓글 더 가져오기</button>}
            </div>
            {syncError && <p role="alert" className="text-xs text-red-600">{syncError}</p>}
          </div>
        )}

        {/* Platform filter */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="seller-comments-filter">
          {[
            { k: 'all',       label: '전체',      count: counts.total },
            { k: 'instagram', label: 'Instagram', count: counts.instagram },
            { k: 'tiktok',    label: 'TikTok',    count: counts.tiktok },
          ].map((f) => (
            <button key={f.k}
              onClick={() => setFilter(f.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-black border transition ${
                platform === f.k
                  ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                  : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-200 border-black/10 hover:border-[#FF3B30]/50'
              }`}
              data-testid={`seller-comments-filter-${f.k}`}>
              {f.label}
              <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                platform === f.k ? 'bg-white/20' : 'bg-black/5 dark:bg-white/10'
              }`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && (
          <div className="py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"/>
          </div>
        )}
        {err && !loading && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm"
               data-testid="seller-comments-error">
            {err}
          </div>
        )}
        {empty && !err && (
          <div className="py-16 text-center" data-testid="seller-comments-empty">
            <MessageCircle size={32} className="mx-auto text-gray-300 mb-3"/>
            <p className="text-sm font-bold text-gray-500">
              {platform === 'tiktok'
                ? '아직 연결된 TikTok 댓글이 없습니다.'
                : platform === 'instagram'
                ? '아직 연결된 Instagram 댓글이 없습니다.'
                : '아직 수집된 댓글이 없습니다.'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              게시물에 새로운 댓글이 달리면 이곳에서 관리할 수 있습니다.
            </p>
          </div>
        )}

        {!loading && !empty && (
          <div className="space-y-3" data-testid="seller-comments-list">
            {grouped.topLevel.map((it) => (
              <CommentCard key={it.post_comment_id} item={it}
                replies={grouped.byParent.get(it.comment_id) || []}
                onReplyClick={onReplyClick}
                replyDraft={drafts[it.post_comment_id] || ''}
                onDraftChange={onDraftChange}
                sending={sendingId === it.post_comment_id}/>
            ))}
            {grouped.orphan.length > 0 && (
              <>
                <div className="pt-2 pb-1">
                  <p className="text-[11px] font-black text-gray-400 tracking-widest uppercase">
                    대댓글 (원댓글 범위 밖)
                  </p>
                </div>
                {grouped.orphan.map((it) => (
                  <CommentCard key={it.post_comment_id} item={it}
                    replies={[]}
                    onReplyClick={onReplyClick}
                    replyDraft={drafts[it.post_comment_id] || ''}
                    onDraftChange={onDraftChange}
                    sending={sendingId === it.post_comment_id}/>
                ))}
              </>
            )}
          </div>
        )}

        <div className="pt-4">
          <Link to="/seller/short-clips"
                className="inline-flex items-center gap-1 text-[11px] text-gray-500 underline"
                data-testid="seller-comments-link-shortclips">
            숏클립 관리로 이동 →
          </Link>
        </div>
      </div>
    </div>
  );
}

